import type { RawProductSample, RawSampleTypeGroup } from './types';

export interface ChainContext {
  parentId: string | null;
  revisionRootId: string | null;
  versionNumber: number;
  supplier: string;
}

export interface AutomationContext extends ChainContext {
  /** The sample type to use for the new request — last sample's type, or first automation step when no samples exist. */
  sampleType: string;
}

const EMPTY: ChainContext = { parentId: null, revisionRootId: null, versionNumber: 1, supplier: '' };

function typeOf(s: RawProductSample | undefined): string {
  if (!s) return '';
  const v = s.sampleTypeObj?._id ?? (typeof s.sampleType === 'object' ? s.sampleType?._id : s.sampleType);
  return String(v ?? '');
}

/**
 * True when `sample` is one of `group`'s samples.
 *
 * Membership is the sample's OWN `sampleTypeGroupId`, mirroring the Samples page list query
 * (fetchOrderSamplesListDAL in data-access-layer/development-order/samples/sample-list.js). Sample
 * TYPE ids cannot stand in for it: groups routinely carry the same types (the Main Sample / Main
 * Sample Licensed merge pre-flight asserts exactly that), so a type match alone would attribute one
 * group's sample to every other group that lists the type.
 *
 * Material samples (`typeOfSampleType: 'other'`) are listed under every group and owned by none.
 * Rows predating the feature carry no group id and `isNewSample` unset — the list query shows those
 * under the default group, so they count there.
 */
function sampleBelongsToGroup(sample: RawProductSample, group: RawSampleTypeGroup): boolean {
  if (sample.typeOfSampleType === 'other') return false;
  const groupId = sample.sampleTypeGroupId ? String(sample.sampleTypeGroupId) : '';
  if (groupId) return groupId === String(group._id);
  return group.isDefault === true && sample.isNewSample !== true;
}

/** The product's samples that belong to `group`, whatever their sample type. */
export function samplesOfGroup(group: RawSampleTypeGroup, samplesList: RawProductSample[]): RawProductSample[] {
  return samplesList.filter((sample) => sampleBelongsToGroup(sample, group));
}

/**
 * The subset of `group`'s samples whose type is one of its automation steps — the set a new
 * automation request in that group would continue.
 */
function automationSamplesOfGroup(group: RawSampleTypeGroup, samplesList: RawProductSample[]): RawProductSample[] {
  const typeIds = new Set((group.sampleTypeAutomation ?? []).map((entry) => entry.sampleType?._id).filter((id): id is string => !!id));
  if (typeIds.size === 0) return [];
  return samplesOfGroup(group, samplesList).filter((sample) => typeIds.has(typeOf(sample)));
}

/**
 * True when `group` runs an automated progression.
 *
 * A group is automated when its FIRST sample type carries at least one automation rule — the same
 * thing the Sample Type Group master shows as configured rows under that sample type, versus a bare
 * "Add Automation" on a sample type that has none. Without a rule on the first step the group never
 * advances a chain of its own, so a further request in it stands alone instead of versioning what
 * the product already holds.
 */
export function isAutomatedSampleTypeGroup(group: RawSampleTypeGroup): boolean {
  const firstStep = (group.sampleTypeAutomation ?? [])[0];
  return Array.isArray(firstStep?.configuration) && firstStep.configuration.length > 0;
}

/**
 * True when the product already holds at least one sample in `group`'s automation set.
 *
 * This is the exact condition under which resolveAutomationContext stops seeding a fresh chain and starts
 * attaching the new request as the next VERSION of an existing sample — which the Samples page then folds
 * into that sample's chain row instead of listing separately. Multi Submit only ever creates a group's
 * first sample, so a group answering true here is dropped from the row's Sample Type Group options.
 */
export function productHasSampleInGroup(group: RawSampleTypeGroup, samplesList: RawProductSample[]): boolean {
  return automationSamplesOfGroup(group, samplesList).length > 0;
}

/**
 * For a new manually-requested sample of `nextTypeId` on a product whose existing samples are `samplesList`,
 * find the "last same-type sample" whose chain root is also of `nextTypeId` (pure same-type chain) and
 * attach the new sample under it. Falls back to "new chain" when no pure-same-type candidate exists.
 */
export function resolveNextSampleContext(samplesList: RawProductSample[], nextTypeId: string): ChainContext {
  if (!nextTypeId) return EMPTY;
  const byId = new Map(samplesList.map((s) => [String(s._id), s]));
  const sameType = samplesList.filter((s) => typeOf(s) === String(nextTypeId));
  const pure = sameType.filter((s) => {
    const rootId = s.revisionRootId ? String(s.revisionRootId) : String(s._id);
    const root = byId.get(rootId) ?? s;
    return typeOf(root) === String(nextTypeId);
  });
  if (pure.length === 0) return { ...EMPTY, supplier: '' };
  const last = pure.reduce((a, b) => ((a.versionNumber ?? 0) >= (b.versionNumber ?? 0) ? a : b));
  return {
    parentId: String(last._id),
    revisionRootId: last.revisionRootId ? String(last.revisionRootId) : String(last._id),
    versionNumber: (last.versionNumber ?? 0) + 1,
    supplier: typeof last.supplier === 'string' ? last.supplier : '',
  };
}

/**
 * On Sample-Type-Group selection: pick the sample type for the new request based on the product's
 * existing samples in that group.
 *
 *   - No existing samples whose type is in the group's automation → first automation step's
 *     sampleType (fresh chain).
 *   - Otherwise → the sample type of the LAST sample CREATED (by MongoDB ObjectID, which encodes
 *     createdAt) among samples in the group. The new request is then attached as the next version
 *     of that sample's same-type chain.
 *
 * Sorting by automation step index is incorrect for non-linear chains (e.g. a Main Sample group
 * with an orphan legacy Proto at step 11 and a live Pre-Production at step 1) — the legacy step
 * wins by index but the live Pre-Production is what the user wants.
 */
export function resolveAutomationContext(
  group: RawSampleTypeGroup,
  samplesList: RawProductSample[],
): AutomationContext {
  const orderedTypeIds = (group.sampleTypeAutomation ?? [])
    .map((a) => a.sampleType?._id)
    .filter((id): id is string => !!id);
  if (orderedTypeIds.length === 0) {
    return { ...EMPTY, sampleType: '' };
  }
  const automationSamples = automationSamplesOfGroup(group, samplesList);
  if (automationSamples.length === 0) {
    const firstTypeId = orderedTypeIds[0]!;
    return { ...EMPTY, sampleType: firstTypeId };
  }
  const last = automationSamples.reduce((a, b) => (String(a._id) >= String(b._id) ? a : b));
  const lastTypeId = typeOf(last);
  const chain = resolveNextSampleContext(samplesOfGroup(group, samplesList), lastTypeId);
  return { ...chain, sampleType: lastTypeId };
}
